import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import config from '../../config';
import { createOnboardingLink } from '../../api/stripeConnect';
import { useGetConnectedAccount } from '../../hooks/paymenthooks';
import { AddCircle } from '@mui/icons-material';

const stripePromise = loadStripe(config.stripePublishableKey || '');

interface AccountOnboardingProps {
  communityId?: string;
  userId?: string;
}



const AccountOnboarding: React.FC<AccountOnboardingProps> = ({ communityId, userId }) => {
  const [clientSecret, setClientSecret] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [pendingAccountId, setPendingAccountId] = useState<string>('');
  const [isCreatingOnboardingLink, setIsCreatingOnboardingLink] = useState(false);

  const {
    accountId: hookAccountId,
    accountData,
    isLoadingAccountData,
    isLoadingPaymentAccount,
    createPaymentAccount,
    reloadAccountData,
  } = useGetConnectedAccount(communityId || '', userId || '');

  const accountId = hookAccountId || pendingAccountId;
  const existingAccount = Boolean(accountData);
  const isLoading = isLoadingAccountData || isLoadingPaymentAccount || isCreatingOnboardingLink;

  // Helper to prettify requirement keys coming from Stripe
  const formatRequirement = (req: string) => {
    const ABBR = ['dob', 'ssn', 'ip', 'url', 'mcc'];
    return req
      .split('.')
      .map(segment =>
        segment
          .split('_')
          .map(word => {
            const lower = word.toLowerCase();
            if (ABBR.includes(lower)) return lower.toUpperCase();
            return lower.charAt(0).toUpperCase() + lower.slice(1);
          })
          .join(' ')
      )
      .join(' → ');
  };

  // console.log("account onboarding")
  // console.log(accountId, accountData)
  // console.log(communityId, userId)

//   var { communityId, authUser } = useAuthState()

  const handleCreateConnectedAccount = async () => {
    setError('');

    try {
      const data = await createPaymentAccount({
        communityId,
        userId,
        type: 'express',
        country: 'US',
        email: userId || '',
        capabilities: {
          card_payments: { requested: true },
          transfers: { requested: true },
        },
      });

      if (data?.accountId) {
        setPendingAccountId(data.accountId);
      }
      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
      }

      if (!data?.accountData) {
        await reloadAccountData();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    }
  };

  const handleCreateOnboardingLink = async () => {
    if (!accountId) {
      setError('Please create an account first');
      return;
    }

    setError('');

    setIsCreatingOnboardingLink(true);
    try {
      const data = await createOnboardingLink({
        accountId,
        returnUrl: `${window.location.origin}/payments`,
        refreshUrl: `${window.location.origin}/stripe/onboarding-refresh`,
      });

      // Redirect to Stripe onboarding
      window.location.href = data.url;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create onboarding link');
    } finally {
      setIsCreatingOnboardingLink(false);
    }
  };

  return (
    <div className="space-y-6">

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {/* Existing Account Information */}
      {existingAccount && accountData && (
        <div className="bg-white border rounded-lg p-6 space-y-6">
          <h3 className="text-lg font-semibold text-gray-900">Payment Account</h3>
          
          {/* Account Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Account Status:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  accountData.charges_enabled ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {accountData.charges_enabled ? 'Active' : 'Pending'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Payouts Enabled:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  accountData.payouts_enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {accountData.payouts_enabled ? 'Yes' : 'No'}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Details Submitted:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  accountData.details_submitted ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {accountData.details_submitted ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Country:</span>
                <span className="text-sm text-gray-900">{accountData.country}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Currency:</span>
                <span className="text-sm text-gray-900">{accountData.default_currency.toUpperCase()}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-700">Account Type:</span>
                <span className="text-sm text-gray-900">{accountData.controller.stripe_dashboard.type}</span>
              </div>
            </div>
          </div>

          {/* Capabilities */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Capabilities</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 font-semibold">Card Payments:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  accountData.capabilities.card_payments === 'active' ? 'bg-green-100 text-green-800' :
                  accountData.capabilities.card_payments === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {accountData.capabilities.card_payments}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-700 font-semibold">Transfers:</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  accountData.capabilities.transfers === 'active' ? 'bg-green-100 text-green-800' :
                  accountData.capabilities.transfers === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {accountData.capabilities.transfers}
                </span>
              </div>
            </div>
          </div>

          {/* Requirements Status */}
          {accountData.requirements.currently_due.length > 0 && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3 flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-4 h-4 bg-yellow-400 text-white text-[10px] font-bold rounded-full">!</span>
                Pending Requirements
              </h4>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-4">
                <p className="text-sm text-yellow-800">
                  The following requirements need to be completed to activate your account:
                </p>
                <div className="flex flex-wrap gap-2">
                  {accountData.requirements.currently_due.map((requirement: string, index: number) => (
                    <span
                      key={index}
                      className="inline-flex items-center bg-yellow-100 text-yellow-800 text-xs font-medium px-3 py-1 rounded-full"
                    >
                      {formatRequirement(requirement)}
                    </span>
                  ))}
                </div>
                {accountData.requirements.current_deadline && (
                  <p className="text-sm text-yellow-800">
                    <strong>Deadline:</strong> {new Date(accountData.requirements.current_deadline * 1000).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          )}


          {/* Business Profile */}
          {accountData.business_profile.name && (
            <div>
              <h4 className="text-md font-medium text-gray-900 mb-3">Business Profile</h4>
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Business Name:</span>
                  <span className="text-sm text-gray-900">{accountData.business_profile.name}</span>
                </div>
                {accountData.business_profile.url && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Website:</span>
                    <a href={accountData.business_profile.url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:text-blue-800">
                      {accountData.business_profile.url}
                    </a>
                  </div>
                )}
                {accountData.business_profile.support_email && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Support Email:</span>
                    <span className="text-sm text-gray-900">{accountData.business_profile.support_email}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Onboarding Actions */}
          <div className="space-y-4">
            <button
              onClick={handleCreateOnboardingLink}
              disabled={isLoading}
              className="w-full bg-primary-400 text-white py-2 px-4 rounded hover:bg-primary-300 disabled:opacity-50"
            >
              {isLoading ? 'Managing Account...' : (accountData.requirements.currently_due.length === 0 && accountData.charges_enabled) ? 'Manage Payment Account' : 'Finish Account Setup'}
            </button>
            
            {accountData.requirements.currently_due.length === 0 && accountData.charges_enabled && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700">
                  ✅ Your account is fully activated and ready to accept payments!
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Account Creation */}
      {!existingAccount && (
        <div className="flex flex-row justify-between space-y-4">
          <button
            onClick={handleCreateConnectedAccount}
            disabled={isLoading}
            className="w-1/5 ml-auto bg-primary-300 text-white py-2 px-4 rounded hover:bg-primary-400 disabled:opacity-50"
          >
            <AddCircle className="w-4 h-4 mr-2" />
            {isLoading ? 'Creating Account...' : 'Create Payment Account'}
          </button>

          {accountId && (
            <div className="bg-green-50 p-4 rounded-lg">
              <p className="text-sm text-green-700">
                Account created successfully! Account ID: {accountId}
              </p>
            </div>
          )}

          {accountId && (
            <button
              onClick={handleCreateOnboardingLink}
              disabled={isLoading}
              className="w-full bg-primary-400 text-white py-2 px-4 rounded hover:bg-primary-300 disabled:opacity-50"
            >
              {isLoading ? 'Creating Onboarding Link...' : 'Start Onboarding Process'}
            </button>
          )}
        </div>
      )}

      {clientSecret && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold mb-4">Embedded Onboarding</h3>
          <div className="border rounded-lg p-4">
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{ clientSecret }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountOnboarding; 